import {ElementRef} from '@angular/core';
import {Subject, Observable} from 'rxjs';
import {takeUntil} from 'rxjs/operators';
import {TranslateService} from '@ngx-translate/core';
import {ServerTranslateService} from 'src/app/services/server-translate.service';
import {EventEmitter} from '@angular/core';
import {
  OpenStreetMapController,
  MapEventsController,
  MapLayersController,
  MapDrawingController,
} from '../openstreetmap.controller';
import {MapInitOptions, MapClickEvent, MapMouseMoveEvent} from '../interfaces';
import {IndexManager} from './index.manager';
import {GeoFileLoaderManager} from './geo-file-loader.manager';
import {VisibilityManager} from './visibility.manager';
import {IBaseLayerConfig} from '../controllers/map-layers.controller';
import {OsmIndexLegendGeoOverlayManager} from './osm-index-legend-geo-overlay.manager';

/**
 * Mutable surface of {@link OpenStreetMapComponent} required for Leaflet bootstrap.
 * Kept explicit to avoid circular imports with the component file.
 */
export interface OsmMapInitHost {
  mapContainer: ElementRef;
  mapOptions: MapInitOptions;
  mapLayers: IBaseLayerConfig;
  translate: TranslateService;
  serverTranslate: ServerTranslateService;
  indexManager: IndexManager;
  geoFileLoaderManager: GeoFileLoaderManager;
  visibilityManager: VisibilityManager;
  destroy$: Subject<void>;
  mapRightClickSubject$: Subject<MapClickEvent>;
  mapClickSubject$: Subject<MapClickEvent>;
  drawingCompleted: EventEmitter<{
    success: boolean;
    error?: any;
    shape?: {
      type: 'polygon' | 'rectangle' | 'box-polygon' | 'custom-polygon';
      coordinates: [number, number][];
      center: {lat: number; lng: number};
      layer: any;
    };
  }>;
  mapReady: EventEmitter<any>;

  isMapInitialized: boolean;
  mapInstance: any;
  osmController: OpenStreetMapController | undefined;
  eventsController: MapEventsController | undefined;
  layersController: MapLayersController | undefined;
  drawingController: MapDrawingController | undefined;

  mapClick$: Observable<MapClickEvent>;
  mapDblClick$: Observable<MapClickEvent>;
  markerClick$: Observable<any>;
  polygonClick$: Observable<MapClickEvent>;
  coordinatesChange$: Observable<[number, number]>;
  mapMove$: Observable<any>;
  mapDragEnd$: Observable<any>;
  mapZoomChange$: Observable<number>;
  mapMouseMove$: Observable<MapMouseMoveEvent>;
  shapesChanged$: Observable<any>;
  monitoringPointsClick$: Observable<any>;

  osmIndexOverlay: OsmIndexLegendGeoOverlayManager;

  currentCursorLat: number | null;
  currentCursorLng: number | null;
  currentZoom: number;

  updateRegionLabelsOnZoom(): void;
  addMonitoringPointsControl(): void;
}

function addTranslatedLeafletZoomControl(
  mapInstance: any,
  translate: TranslateService,
  destroy$: Subject<void>,
  position = 'topright',
): void {
  const L = (window as any).L;
  if (!mapInstance || !L?.control?.zoom) {
    return;
  }

  const getTitles = () => ({
    zoomInTitle: translate.instant('Zoom In'),
    zoomOutTitle: translate.instant('Zoom Out'),
  });

  const zoomControl = L.control.zoom({
    position,
    ...getTitles(),
  });
  zoomControl.addTo(mapInstance);

  const applyTitles = () => {
    const {zoomInTitle, zoomOutTitle} = getTitles();
    if (zoomControl._zoomInButton) {
      zoomControl._zoomInButton.title = zoomInTitle;
      zoomControl._zoomInButton.setAttribute('aria-label', zoomInTitle);
    }
    if (zoomControl._zoomOutButton) {
      zoomControl._zoomOutButton.title = zoomOutTitle;
      zoomControl._zoomOutButton.setAttribute('aria-label', zoomOutTitle);
    }
  };

  translate.onLangChange.pipe(takeUntil(destroy$)).subscribe(() => applyTitles());
}

export function loadLeafletFromCDNForOsm(host: OsmMapInitHost): void {
  const leafletCSS = document.createElement('link');
  leafletCSS.rel = 'stylesheet';
  leafletCSS.href = 'https://unpkg.com/leaflet@1.9.4/dist/leaflet.css';
  document.head.appendChild(leafletCSS);

  const leafletJS = document.createElement('script');
  leafletJS.src = 'https://unpkg.com/leaflet@1.9.4/dist/leaflet.js';
  leafletJS.onload = () => {
    const clusterJS = document.createElement('script');
    clusterJS.src = 'https://unpkg.com/leaflet.markercluster@1.4.1/dist/leaflet.markercluster.js';
    clusterJS.onload = () => {
      setTimeout(() => initializeOpenStreetMap(host), 100);
    };
    document.head.appendChild(clusterJS);
  };
  document.head.appendChild(leafletJS);

  const clusterCSS = document.createElement('link');
  clusterCSS.rel = 'stylesheet';
  clusterCSS.href = 'https://unpkg.com/leaflet.markercluster@1.4.1/dist/MarkerCluster.css';
  document.head.appendChild(clusterCSS);

  const clusterDefaultCSS = document.createElement('link');
  clusterDefaultCSS.rel = 'stylesheet';
  clusterDefaultCSS.href = 'https://unpkg.com/leaflet.markercluster@1.4.1/dist/MarkerCluster.Default.css';
  document.head.appendChild(clusterDefaultCSS);
}

export function tryInitializeOpenStreetMapWhenReady(host: OsmMapInitHost): void {
  const Lpresent = () => typeof (window as any).L !== 'undefined';
  const containerVisible = () => {
    try {
      const el = host.mapContainer?.nativeElement;
      return el && el.offsetWidth > 0 && el.offsetHeight > 0;
    } catch {
      return false;
    }
  };

  const doInit = () => {
    try {
      initializeOpenStreetMap(host);
      setTimeout(() => {
        try {
          (host as any).mapInstance?.invalidateSize();
        } catch {
          /* ignore */
        }
      }, 200);
      if ((window as any).ResizeObserver && host.mapContainer) {
        const ro = new (window as any).ResizeObserver(() => {
          try {
            (host as any).mapInstance?.invalidateSize();
          } catch {
            /* ignore */
          }
        });
        ro.observe(host.mapContainer.nativeElement);
      } else {
        window.addEventListener('resize', () => {
          try {
            (host as any).mapInstance?.invalidateSize();
          } catch {
            /* ignore */
          }
        });
      }
    } catch (err) {
      console.error('Map initialization...', err);
    }
  };

  if (Lpresent() && containerVisible()) {
    doInit();
    return;
  }

  if ((window as any).ResizeObserver && host.mapContainer) {
    const observer = new (window as any).ResizeObserver(() => {
      if (Lpresent() && containerVisible()) {
        observer.disconnect();
        doInit();
      }
    });
    observer.observe(host.mapContainer.nativeElement);
    const checkLeafletInterval = setInterval(() => {
      if (Lpresent() && containerVisible()) {
        clearInterval(checkLeafletInterval);
        try {
          observer.disconnect();
        } catch {
          /* ignore */
        }
        doInit();
      }
    }, 200);
    setTimeout(() => clearInterval(checkLeafletInterval), 10000);
    return;
  }

  let attempts = 0;
  const interval = setInterval(() => {
    attempts++;
    if (Lpresent() && containerVisible()) {
      clearInterval(interval);
      doInit();
    } else if (attempts > 30) {
      clearInterval(interval);
      if (Lpresent()) {
        doInit();
      }
    }
  }, 200);
}

export function initializeOpenStreetMap(host: OsmMapInitHost): void {
  if (typeof (window as any).L === 'undefined') {
    return;
  }

  const c = host as any;

  c.osmController = new OpenStreetMapController(c.translate);
  const mapElement = c.mapContainer.nativeElement;
  c.osmController.initialize(mapElement, c.mapOptions);

  c.eventsController = c.osmController.getEventsController();
  c.drawingController = c.osmController.getDrawingController();
  c.mapInstance = c.osmController.getMapInstance();

  c.layersController = c.osmController.getLayersController();
  c.layersController.setServerTranslate(c.serverTranslate as ServerTranslateService);

  c.layersController.setBaseLayerUrls({
    plan: c.mapLayers['plan']?.url,
    sentinel: c.mapLayers['sentinel']?.url,
    google: c.mapLayers['google']?.url,
    landsat: c.mapLayers['landsat']?.url,
  });

  c.drawingController.setLayersController(c.layersController);

  c.mapClick$ = c.eventsController.mapClick$;
  c.mapDblClick$ = c.eventsController.mapDblClick$;
  c.markerClick$ = c.eventsController.markerClick$;
  c.polygonClick$ = c.eventsController.polygonClick$;
  c.coordinatesChange$ = c.eventsController.coordinatesChange$ as Observable<[number, number]>;
  c.mapMove$ = c.eventsController.mapMove$;
  c.mapDragEnd$ = c.eventsController.mapDragEnd$;
  c.mapZoomChange$ = c.eventsController.mapZoomChange$ as Observable<number>;
  c.mapMouseMove$ = c.eventsController.mapMouseMove$;
  c.shapesChanged$ = c.eventsController.shapesChanged$;
  c.monitoringPointsClick$ = c.eventsController.monitoringPointsClick$;

  c.eventsController.mapRightClick$
    .pipe(takeUntil(c.destroy$))
    .subscribe((event: MapClickEvent) => {
      c.mapRightClickSubject$.next(event);
    });

  c.eventsController.mapClick$
    .pipe(takeUntil(c.destroy$))
    .subscribe((event: MapClickEvent) => {
      c.mapClickSubject$.next(event);
    });

  c.eventsController.vectorTileFeatureClick$
    .pipe(takeUntil(c.destroy$))
    .subscribe((payload: {feature: any; layerId: string; latlng: any}) => {
      const {feature, layerId, latlng} = payload;
      console.log('Vector tile feature clicked:', feature, 'Layer:', layerId, 'at', latlng);
    });

  c.isMapInitialized = true;

  c.mapInstance.on('zoomend', () => {
    c.updateRegionLabelsOnZoom();
  });

  c.indexManager.initialize(c.mapInstance);
  c.eventsController.setMapInstance(c.mapInstance);
  c.geoFileLoaderManager.initialize(c.mapInstance, c.eventsController);

  c.visibilityManager.attach({
    mapOptions: c.mapOptions,
    getLayersController: () => c.layersController,
    getDrawingController: () => c.drawingController,
    getMapInstance: () => c.mapInstance,
    geoFileLoaderManager: c.geoFileLoaderManager,
  });

  c.osmIndexOverlay.startLayerMonitoring();

  c.mapMouseMove$
    .pipe(takeUntil(c.destroy$))
    .subscribe((event: MapMouseMoveEvent) => {
      c.currentCursorLat = event.lat;
      c.currentCursorLng = event.lng;
    });

  c.mapZoomChange$
    .pipe(takeUntil(c.destroy$))
    .subscribe((zoom: number) => {
      c.currentZoom = zoom;
    });

  c.drawingController.shapeCreated$
    .pipe(takeUntil(c.destroy$))
    .subscribe({
      next: (shape: any) => {
        c.drawingCompleted.emit({
          success: true,
          shape: shape,
        });
        c.mapOptions.drawingMode = false;
      },
      error: (error: any) => {
        c.drawingCompleted.emit({
          success: false,
          error: error,
        });
        c.mapOptions.drawingMode = false;
      },
    });

  try {
    if (c.mapOptions?.showControls !== false && !c.mapOptions?.minimalControls) {
      if (c.mapInstance && (window as any).L && typeof (window as any).L.control?.zoom === 'function') {
        addTranslatedLeafletZoomControl(c.mapInstance, c.translate, c.destroy$);
      }
      try {
        c.addMonitoringPointsControl();
      } catch (err) {
        console.error('Error adding monitoring points control:', err);
      }
    }
  } catch (err) {
    console.error('Error adding zoom control:', err);
  }

  setTimeout(() => c.mapReady.emit(c.mapInstance), 0);
}
